/**
 * Presentation helpers shared by the dashboard, onboarding wizard, and data views.
 * Pure formatting only — no query or domain logic.
 *
 * Localization is a product requirement (PRODUCT.md): formatters use `Intl` so
 * currency, numbers, and dates render natively per locale. The active locale is
 * kept in a module-level accessor the i18n provider syncs (same pattern as the
 * session-token accessor), so every call site is locale-aware without threading
 * the tag through its signature. An explicit `locale` argument still wins when a
 * caller passes one.
 */

/** Module-level active locale tag, kept in sync by the i18n provider. */
let activeLocale: string | undefined;

/** Set the active locale used by every formatter when no explicit tag is passed. */
export function setActiveLocale(tag: string): void {
  activeLocale = tag;
}

function resolve(locale?: string): string | undefined {
  return locale ?? activeLocale;
}

/** Format a money amount with its currency code, or an em-dash when `null` (pending). */
export function money(
  amount: number | null | undefined,
  currency?: string,
  locale?: string,
): string {
  if (amount === null || amount === undefined) return '—';
  const loc = resolve(locale);
  // When we know the currency, use Intl's currency formatting so the symbol and
  // digit grouping follow the locale (JOD shows 3 fraction digits in ar-JO).
  if (currency) {
    try {
      return new Intl.NumberFormat(loc, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      }).format(amount);
    } catch {
      // Unknown/invalid currency code — fall through to plain number + code.
    }
  }
  const value = amount.toLocaleString(loc, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${value} ${currency}` : value;
}

/** Format a percentage number (already in [0,100]) with up to two decimals, or em-dash. */
export function pct(value: number | null | undefined, locale?: string): string {
  if (value === null || value === undefined) return '—';
  return `${value.toLocaleString(resolve(locale), { maximumFractionDigits: 2 })}%`;
}

/** Format a plain count/number with locale digit grouping. */
export function num(value: number | null | undefined, locale?: string): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(resolve(locale));
}

/**
 * Format a number to a FIXED number of fraction digits (default 2), locale-aware
 * so Arabic renders ar-JO numerals. The locale-aware stand-in for a raw
 * `n.toFixed(2)`: `dec(4.07)` ⇒ `"4.07"` (en) / `"٤٫٠٧"` (ar-JO). Null ⇒ em-dash.
 */
export function dec(
  value: number | null | undefined,
  digits = 2,
  locale?: string,
): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(resolve(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Format a SIGNED fixed-decimal number (always shows `+` for non-negatives), the
 * locale-aware stand-in for `` `${v >= 0 ? '+' : ''}${v.toFixed(2)}` ``. The sign
 * is prefixed explicitly (Intl's `signDisplay` is uneven across engines/locales);
 * the magnitude goes through {@link dec} so Arabic gets native numerals. A
 * negative value already carries its own locale minus sign from `dec`. Null ⇒
 * em-dash.
 */
export function signed(
  value: number | null | undefined,
  digits = 2,
  locale?: string,
): string {
  if (value === null || value === undefined) return '—';
  const body = dec(value, digits, locale);
  return value >= 0 ? `+${body}` : body;
}

/** Format an ISO timestamp as a short human date (locale-aware), falling back to the raw string. */
export function shortDate(iso: string, locale?: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(resolve(locale));
}

/**
 * Format an ISO timestamp as a short locale-aware date + time (e.g. "1 Aug,
 * 10:00"), for event logs like the ingestion run history where the clock time
 * matters, not just the day. Falls back to the raw string when unparseable.
 */
export function dateTime(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(resolve(locale), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format an ISO period `[start, end)` as `start → end`. */
export function period(start: string, end: string, locale?: string): string {
  return `${shortDate(start, locale)} → ${shortDate(end, locale)}`;
}

/**
 * Format a derivation period `[start, end]` as a compact, locale-aware
 * month-span label, e.g. "Apr – Sep 2026" (same year) or "Dec 2025 – Mar 2026"
 * (year boundary). Both bounds are ISO timestamps; returns `null` when either
 * is absent (shown as unavailable, never a fabricated range). The en-dash is a
 * real range dash, not a hyphen.
 */
export function periodMonths(
  start: string | null | undefined,
  end: string | null | undefined,
  locale?: string,
): string | null {
  if (!start || !end) return null;
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const loc = resolve(locale);
  const month = (d: Date) => d.toLocaleDateString(loc, { month: 'short' });
  const year = (d: Date) => d.toLocaleDateString(loc, { year: 'numeric' });
  // Same calendar year ⇒ name the year once at the end ("Apr – Sep 2026").
  if (from.getFullYear() === to.getFullYear() && month(from) === month(to)) {
    return `${month(from)} ${year(to)}`;
  }
  if (from.getFullYear() === to.getFullYear()) {
    return `${month(from)} – ${month(to)} ${year(to)}`;
  }
  return `${month(from)} ${year(from)} – ${month(to)} ${year(to)}`;
}

/** Format a `YYYY-MM-DD` day as a short "6 Sep"-style label (pinned to UTC; unparseable ⇒ the raw string). */
export function dayMonth(day: string, locale?: string): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(resolve(locale), { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * Format an EXACT date range for the summary header, e.g. "1 Sep – 30 Sep 2026"
 * or "1 Apr – 30 Sep 2026". The reference period is a half-open window
 * `[start, end)` where `end` is the first instant of the NEXT month, so the
 * INCLUSIVE last day shown is `end − 1 day`. Returns `null` when either bound is
 * absent/invalid (shown as "unavailable", never a fabricated range).
 */
export function periodDates(
  start: string | null | undefined,
  end: string | null | undefined,
  locale?: string,
): string | null {
  if (!start || !end) return null;
  const from = new Date(start);
  const endExclusive = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(endExclusive.getTime())) return null;
  // Inclusive last day = the day before the exclusive end bound. All formatting
  // is pinned to UTC so the rendered day matches the window's UTC boundaries
  // regardless of the viewer's zone (the period is a UTC month window).
  const to = new Date(endExclusive.getTime() - 24 * 60 * 60 * 1000);
  const loc = resolve(locale);
  const dayMonth = (d: Date) =>
    d.toLocaleDateString(loc, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const year = (d: Date) => d.toLocaleDateString(loc, { year: 'numeric', timeZone: 'UTC' });
  if (from.getUTCFullYear() === to.getUTCFullYear()) {
    return `${dayMonth(from)} – ${dayMonth(to)} ${year(to)}`;
  }
  return `${dayMonth(from)} ${year(from)} – ${dayMonth(to)} ${year(to)}`;
}

/**
 * Narrow a ts-rest query/mutation error to its HTTP status, when it is a known
 * status-coded response (vs a thrown network `Error`). ts-rest v5 surfaces
 * non-2xx responses on `error` as `{ status, body }`; a transport failure is a
 * plain `Error` with no `status`. Returns the numeric status or `undefined`.
 */
export function errorStatus(error: unknown): number | undefined {
  if (
    error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    return (error as { status: number }).status;
  }
  return undefined;
}

/** True when the error is the not-yet-onboarded block (`409`, Req 1.2). */
export function isOnboardingRequired(error: unknown): boolean {
  return errorStatus(error) === 409;
}

/**
 * True when a reconciliation read answered `404` — the EXPECTED "no report yet"
 * state (the payout/period is simply not reconciled), not a failure. The read
 * side surfaces it so the UI can show "not reconciled yet" rather than an error
 * (clarity-reconciliation Req 8.4).
 */
export function isNotReconciled(error: unknown): boolean {
  return errorStatus(error) === 404;
}

/**
 * Map the per-order `accuracy` axis to its user-facing label
 * (clarity-reconciliation Req 4.1). The raw enum is an implementation detail;
 * the dashboard shows the four human labels. Keyed by the `Accuracy` enum so a
 * new value is a compile error here, not a silent fallthrough.
 */
const ACCURACY_LABELS: Record<string, string> = {
  pending: 'Pending',
  platform_estimated: 'Platform estimated',
  finalized: 'Finalized',
  ai_estimated: 'AI estimated',
};

/** The user-facing label for an order's accuracy axis (Req 4.1). */
export function accuracyLabel(accuracy: string): string {
  return ACCURACY_LABELS[accuracy] ?? accuracy;
}

/**
 * Map a reconciliation `verdict` to its user-facing label. The engine's
 * `reconciled` reads as "Reconciled" (the design's verdict chip historically
 * used "verified"; the contract enum is the source of truth here).
 */
const VERDICT_LABELS: Record<string, string> = {
  reconciled: 'Reconciled',
  discrepant: 'Discrepant',
  incomplete: 'Incomplete',
};

/** The user-facing label for a reconciliation verdict. */
export function verdictLabel(verdict: string): string {
  return VERDICT_LABELS[verdict] ?? verdict;
}
